import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Banknote,
  ChevronLeft,
  ChevronRight,
  Headphones,
  Instagram,
  ShoppingBag,
  Truck,
  UserRound,
  Footprints
} from 'lucide-react';
import axios from 'axios';

import { imgUrl, businessInfo } from '../data/mockData';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;
const SLIDE_INTERVAL = 8000;

const HERO_SLIDES = [
  {
    image: '/images/home/hero-femra.jpg',
    eyebrow: 'LOR95 BOUTIQUE',
    title: 'Ngrini Stilin Tuaj',
    description:
      'Koleksione të përzgjedhura për femra, ku eleganca takohet me stilin modern.',
    primaryText: 'ZBULO KOLEKSIONIN',
    primaryTo: '/collections?gender=Femra'
  },
  {
    image: '/images/home/hero-meshkuj.jpg',
    eyebrow: 'KOLEKSIONI PËR MESHKUJ',
    title: 'Stil që Ju Përfaqëson',
    description:
      'Modele moderne për meshkuj, të zgjedhura për çdo ditë dhe çdo moment.',
    primaryText: 'ZBULO MESHKUJ',
    primaryTo: '/collections?gender=Meshkuj'
  },
  {
    image: '/images/home/hero-kepuce.jpg',
    eyebrow: 'ATLETE & KËPUCË',
    title: 'Hapi me Stil',
    description:
      'Komoditet dhe stil në çdo hap me përzgjedhjen tonë të këpucëve.',
    primaryText: 'ZBULO KËPUCËT',
    primaryTo: '/collections?category=Këpucë'
  }
];

const CATEGORY_CARDS = [
  {
    title: 'PËR FEMRA',
    subtitle: 'Zbulo koleksionin',
    image: '/images/home/category-femra.jpg',
    to: '/collections?gender=Femra',
    icon: UserRound
  },
  {
    title: 'PËR MESHKUJ',
    subtitle: 'Zbulo koleksionin',
    image: '/images/home/category-meshkuj.jpg',
    to: '/collections?gender=Meshkuj',
    icon: UserRound
  },
  {
    title: 'AKSESORË & PARFUME',
    subtitle: 'Detajet dhe aromat që bëjnë diferencën',
    image: '/images/home/category-aksesore.jpg',
    to: '/collections?categories=Aksesorë,Parfume',
    icon: ShoppingBag
  },
  {
    title: 'ATLETE & KËPUCË',
    subtitle: 'Komoditet & stil në çdo hap',
    image: '/images/home/category-kepuce.jpg',
    to: '/collections?category=Këpucë',
    icon: Footprints
  }
];

const SERVICE_ITEMS = [
  {
    title: 'DORËZIM I SHPEJTË',
    text: 'Në të gjithë Shqipërinë',
    icon: Truck
  },
  {
    title: 'PRODUKTE ORIGJINALE',
    text: 'Cilësi premium e garantuar',
    icon: BadgeCheck
  },
  {
    title: 'PAGESA NË DORËZIM',
    text: 'Paguani kur merrni porosinë',
    icon: Banknote
  },
  {
    title: 'MBËSHTETJE',
    text: 'Jemi këtu për t’ju ndihmuar',
    icon: Headphones
  }
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/api/products`)
      .then((res) => setProducts(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrentSlide((current) => (current + 1) % HERO_SLIDES.length);
    }, SLIDE_INTERVAL);

    return () => window.clearInterval(timer);
  }, []);

  const collectionProducts = useMemo(() => {
    const preferred = products.filter(
      (product) =>
        product.newArrival
        ||
        product.featured
        ||
        product.bestSeller
    );

    return (
      preferred.length
        ? preferred
        : products
    ).slice(0, 6);
  }, [products]);


  const saleProducts = useMemo(() => {
    return products
      .filter(
        (product) =>
          product.salePrice != null
          &&
          Number(product.salePrice) > 0
          &&
          Number(product.salePrice) < Number(product.price)
      )
      .slice(0, 6);
  }, [products]);

  const instagramProducts = useMemo(
    () => products.slice(0, 5),
    [products]
  );

  const nextSlide = () => {
    setCurrentSlide((current) => (current + 1) % HERO_SLIDES.length);
  };

  const previousSlide = () => {
    setCurrentSlide(
      (current) =>
        (current - 1 + HERO_SLIDES.length) % HERO_SLIDES.length
    );
  };

  const slide = HERO_SLIDES[currentSlide];

  const clothingStoreSchema = {
    "@context": "https://schema.org",
    "@type": "ClothingStore",
    "name": "Lor95 Boutique",
    "alternateName": [
      "Lor 95 Boutique",
      "Lor95",
      "Lor 95"
    ],
    "url": "https://lor95.com",
    "hasMap": "https://maps.app.goo.gl/KNEhbnu6hkvZpY7z8",
    "logo": "https://lor95.com/lor95.png",
    "image": "https://lor95.com/images/storefront.jpg",
    "description":
      "Lor95 Boutique në Berat - veshje për femra dhe meshkuj, këpucë, veshje sportive, aksesorë dhe koleksione të përzgjedhura.",
    "telephone": "+355 69 352 0111",
    "priceRange": "€€",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Rruga Desaret",
      "addressLocality": "Berat",
      "postalCode": "5001",
      "addressCountry": "AL"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 40.70184573823659,
      "longitude": 19.961926976438416
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "09:00",
        "closes": "21:00"
      }
    ],
    "sameAs": [
      "https://www.instagram.com/lor_95_boutique/"
    ]
  };

  return (
    <div className="min-h-screen bg-[#f8f4ee]">

      <SEO
        title="Lor95 Boutique"
        description="Lor95 Boutique - modë online për femra dhe meshkuj, veshje, këpucë, veshje sportive, aksesorë dhe parfume. Porosit online me dërgesa në të gjithë Shqipërinë."
        path="/"
        image="https://lor95.com/images/storefront.jpg"
      />

      {/* ============================================================
          HERO
      ============================================================ */}
      <section className="relative min-h-[760px] h-[100svh] sm:h-[92vh] max-h-[980px] overflow-hidden bg-black">

        {HERO_SLIDES.map((item, index) => (
          <div
            key={item.image}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              currentSlide === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/*
              MOBILE:
              - layer blurred mbush ekranin
              - fotoja kryesore është object-contain që të mos pritet modeli

              DESKTOP:
              - object-cover për hero full-screen
            */}
            <img
              src={item.image}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover scale-110 blur-xl opacity-45 sm:hidden"
            />

            <img
              src={item.image}
              alt={item.title}
              className={`absolute inset-0 h-full w-full object-contain object-center sm:object-cover sm:object-center transition-transform duration-[8000ms] ease-out ${
                currentSlide === index ? 'sm:scale-105' : 'sm:scale-100'
              }`}
            />

            {/* Overlay më i lehtë që fotoja të mos duket e zezë */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-black/5 sm:from-black/65 sm:via-black/25 sm:to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/15" />
          </div>
        ))}

        <div className="relative z-10 max-w-7xl mx-auto h-full px-5 sm:px-8 lg:px-10 flex items-center">
          <div className="w-full max-w-xl pt-24 sm:pt-20 pb-20">

            <p className="text-[#d1a24e] text-[11px] sm:text-sm font-semibold tracking-[0.20em] sm:tracking-[0.24em] uppercase mb-4 sm:mb-5">
              {slide.eyebrow}
            </p>

            <h1 className="font-serif text-white text-[46px] leading-[0.94] sm:text-7xl lg:text-[88px] sm:leading-[0.92] font-bold tracking-[-0.03em] drop-shadow-[0_3px_10px_rgba(0,0,0,0.55)]">
              {slide.title}
            </h1>

            <div className="w-20 sm:w-24 h-[2px] bg-[#c8953d] mt-6 sm:mt-7 mb-6 sm:mb-7" />

            <p className="text-white text-base sm:text-xl leading-relaxed max-w-lg drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
              {slide.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-7 sm:mt-9">
              <Button
                asChild
                className="h-14 px-7 rounded-sm bg-[#c8943d] hover:bg-[#b58434] text-white font-semibold tracking-wide"
              >
                <Link to={slide.primaryTo}>
                  {slide.primaryText}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>

              <Button
                asChild
                variant="outline"
                className="h-14 px-7 rounded-sm border-[#c8943d] bg-black/25 text-white hover:bg-[#c8943d] hover:text-white hover:border-[#c8943d]"
              >
                <Link to="/visit">
                  NA VIZITONI
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={previousSlide}
          aria-label="Fotoja e mëparshme"
          className="absolute z-20 left-3 sm:left-7 top-[53%] sm:top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-white/40 bg-black/35 backdrop-blur-md text-white flex items-center justify-center hover:bg-black/55 transition"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>

        <button
          type="button"
          onClick={nextSlide}
          aria-label="Fotoja tjetër"
          className="absolute z-20 right-3 sm:right-7 top-[53%] sm:top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-white/40 bg-black/35 backdrop-blur-md text-white flex items-center justify-center hover:bg-black/55 transition"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        <div className="absolute z-20 left-1/2 bottom-5 sm:bottom-7 -translate-x-1/2 flex items-center gap-3">
          {HERO_SLIDES.map((item, index) => (
            <button
              key={item.image}
              type="button"
              aria-label={`Shko te fotoja ${index + 1}`}
              onClick={() => setCurrentSlide(index)}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                currentSlide === index
                  ? 'w-8 bg-[#c8953d]'
                  : 'w-2.5 bg-white/85 hover:bg-white'
              }`}
            />
          ))}
        </div>
      </section>

      {/* ============================================================
          CATEGORY COLLECTIONS
      ============================================================ */}
      <section className="py-16 lg:py-20 bg-[#f7f2eb]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">

          <div className="text-center mb-10">
            <p className="text-[#b68434] text-xs font-bold tracking-[0.22em] uppercase">
              Koleksioni i Ri
            </p>

            <div className="w-12 h-[2px] bg-[#c8953d] mx-auto mt-3 mb-4" />

            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#171717]">
              Zbulo Stilin që të Përfaqëson
            </h2>

            <p className="text-gray-600 text-base sm:text-lg mt-3">
              Modele të reja, cilësi premium dhe dizajn modern për çdo moment.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CATEGORY_CARDS.map((category) => {
              const Icon = category.icon;

              return (
                <Link
                  key={category.title}
                  to={category.to}
                  className="group relative h-[320px] overflow-hidden rounded-[10px] bg-black shadow-sm"
                >
                  <img
                    src={category.image}
                    alt={category.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/10 to-transparent" />

                  <div className="absolute left-5 right-5 bottom-5 flex gap-3 items-end">
                    <div className="w-10 h-10 rounded-full border border-[#c8953d]/80 bg-black/30 text-[#d4a552] flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>

                    <div>
                      <h3 className="text-white font-bold text-sm tracking-wide">
                        {category.title}
                      </h3>

                      <p className="text-white/85 text-xs mt-1">
                        {category.subtitle}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          SERVICES
      ============================================================ */}
      <section className="bg-white border-y border-[#eee4d7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICE_ITEMS.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.title}
                  className={`flex items-center gap-4 py-4 lg:px-7 ${
                    index > 0 ? 'lg:border-l lg:border-[#e8ded0]' : ''
                  }`}
                >
                  <Icon className="h-9 w-9 text-[#c8953d] flex-shrink-0" />

                  <div>
                    <p className="text-sm font-bold text-gray-900 tracking-wide">
                      {item.title}
                    </p>

                    <p className="text-xs text-gray-500 mt-1">
                      {item.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          PRODUCTS
      ============================================================ */}
      <section className="py-16 lg:py-20 bg-[#f7f2eb]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">

          <div className="flex items-end justify-between gap-6 mb-8">
            <div>
              <p className="text-[#b68434] text-xs font-bold tracking-[0.22em] uppercase">
                Koleksioni i Ri
              </p>

              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900 mt-2">
                Zgjedhjet e Fundit
              </h2>
            </div>

            <Link
              to="/collections"
              className="hidden sm:inline-flex items-center gap-2 text-xs font-bold tracking-wider text-[#9d722f] hover:text-gray-900"
            >
              SHIKO TË GJITHA
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {collectionProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {collectionProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="group"
                >
                  <div className="relative overflow-hidden rounded-lg bg-[#ede8e0] aspect-[3/4]">
                    <img
                      src={imgUrl(product.image || product.images?.[0]?.url)}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />

                    {(
                      product.salePrice != null
                      &&
                      Number(product.salePrice) > 0
                      &&
                      Number(product.salePrice) < Number(product.price)
                    ) && (
                      <span className="absolute top-3 left-3 bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded-sm">
                        -{Math.round(
                          (
                            (
                              Number(product.price)
                              -
                              Number(product.salePrice)
                            )
                            /
                            Number(product.price)
                          )
                          *
                          100
                        )}%
                      </span>
                    )}

                    <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/85 backdrop-blur flex items-center justify-center text-[#b68434]">
                      ♡
                    </span>
                  </div>

                  <h3 className="text-sm text-gray-800 mt-3 line-clamp-1">
                    {product.name}
                  </h3>

                  {(
                    product.salePrice != null
                    &&
                    Number(product.salePrice) > 0
                    &&
                    Number(product.salePrice) < Number(product.price)
                  ) ? (
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-xs text-gray-400 line-through">
                        {(product.price / 100).toFixed(0)} Lek
                      </span>

                      <span className="text-sm font-bold text-red-600">
                        {(product.salePrice / 100).toFixed(0)} Lek
                      </span>
                    </div>
                  ) : (
                    <p className="text-sm font-bold text-gray-950 mt-1">
                      {(product.price / 100).toFixed(0)} Lek
                    </p>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              Produktet do të shfaqen këtu sapo të ngarkohen.
            </div>
          )}

          <div className="sm:hidden mt-8 text-center">
            <Link
              to="/collections"
              className="inline-flex items-center gap-2 text-xs font-bold tracking-wider text-[#9d722f]"
            >
              SHIKO TË GJITHA
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
          PRODUCTS ON SALE
      ============================================================ */}
      <section className="py-16 lg:py-20 bg-white border-t border-[#eee4d7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">

          <div className="flex items-end justify-between gap-6 mb-8">
            <div>
              <p className="text-red-600 text-xs font-bold tracking-[0.22em] uppercase">
                Oferta
              </p>

              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900 mt-2">
                Produkte me Ulje
              </h2>

              <p className="text-gray-500 text-sm mt-2">
                Zgjidhni nga produktet tona aktualisht me çmim të reduktuar.
              </p>
            </div>

            <Link
              to="/collections?category=Produkte%20me%20Ulje"
              className="hidden sm:inline-flex items-center gap-2 text-xs font-bold tracking-wider text-red-600 hover:text-gray-900"
            >
              SHIKO TË GJITHA
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {saleProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {saleProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="group"
                >
                  <div className="relative overflow-hidden rounded-lg bg-[#ede8e0] aspect-[3/4]">
                    <img
                      src={imgUrl(product.image || product.images?.[0]?.url)}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />

                    <span className="absolute top-3 left-3 bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded-sm">
                      -{Math.round(
                        (
                          (
                            Number(product.price)
                            -
                            Number(product.salePrice)
                          )
                          /
                          Number(product.price)
                        )
                        *
                        100
                      )}%
                    </span>

                    <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/85 backdrop-blur flex items-center justify-center text-[#b68434]">
                      ♡
                    </span>
                  </div>

                  <h3 className="text-sm text-gray-800 mt-3 line-clamp-1">
                    {product.name}
                  </h3>

                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-xs text-gray-400 line-through">
                      {(product.price / 100).toFixed(0)} Lek
                    </span>

                    <span className="text-sm font-bold text-red-600">
                      {(product.salePrice / 100).toFixed(0)} Lek
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              Ende nuk ka produkte me ulje.
            </div>
          )}

          <div className="sm:hidden mt-8 text-center">
            <Link
              to="/collections?category=Produkte%20me%20Ulje"
              className="inline-flex items-center gap-2 text-xs font-bold tracking-wider text-red-600"
            >
              SHIKO TË GJITHA
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>


      {/* ============================================================
          INSTAGRAM
      ============================================================ */}
      <section className="bg-[#0d0d0c] text-white py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">

          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-7 items-center">

            <div>
              <div className="flex items-center gap-2 text-[#c8953d] text-xs font-semibold tracking-wider">
                <Instagram className="h-5 w-5" />
                NA NDIQNI NË INSTAGRAM
              </div>

              <p className="font-serif text-3xl text-white mt-3">
                @lor_95_boutique
              </p>

              <a
                href={businessInfo.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-5 border border-[#c8953d] px-5 py-3 text-xs font-semibold hover:bg-[#c8953d] transition"
              >
                NA NDIQNI
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {instagramProducts.length > 0
                ? instagramProducts.map((product) => (
                    <a
                      key={product.id}
                      href={businessInfo.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative aspect-[4/5] overflow-hidden rounded-md bg-neutral-900"
                    >
                      <img
                        src={imgUrl(product.image || product.images?.[0]?.url)}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />

                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />

                      <Instagram className="absolute right-3 top-3 h-5 w-5 text-white opacity-90" />
                    </a>
                  ))
                : CATEGORY_CARDS.slice(0, 5).map((category) => (
                    <a
                      key={category.title}
                      href={businessInfo.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative aspect-[4/5] overflow-hidden rounded-md"
                    >
                      <img
                        src={category.image}
                        alt={category.title}
                        className="w-full h-full object-cover"
                      />
                    </a>
                  ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
